import {
  Vec2,
  ARENA_WIDTH,
  ARENA_HEIGHT,
  SHIP_RADIUS,
  PLAYER_COLORS,
  UI_TEXT_COLOR,
  UI_ACCENT_COLOR,
  CRATE_COLOR,
  CRATE_OUTLINE_COLOR,
  ASTEROID_COLOR,
  ASTEROID_OUTLINE_COLOR,
  STATION_COLOR,
  STATION_OUTLINE_COLOR,
  CRATE_SIZE,
  STATION_RADIUS,
  STATION_TURN_IN_RADIUS,
  FIXED_DT,
  NPC_COUNT,
  MAX_PLAYERS,
  createWorld,
  runSystems,
  generateNpcInputs,
  type World,
  type InputState,
  type PlayerId,
  type LobbyPlayer,
  type MatchResult,
  type PayoutMode,
} from '@mine-or-die/shared';
import { GameWindow, InputManager } from './platform/index.js';
import { Renderer2D, GameLoop, EffectsManager, AudioManager } from './engine/index.js';
import { NetClient } from './engine/net-client.js';
import {
  connect as connectWallet,
  disconnect as disconnectWallet,
  isConnected as isWalletConnected,
  getAddress as getWalletAddress,
  getUSDCBalance,
  formatUSDC,
  getAvailableWallets,
  getWalletDisplayName,
  stake as stakeToEscrow,
  hasStaked as checkHasStaked,
  // Admin functions
  isAdmin,
  getAccumulatedDevFees,
  getContractUSDCBalance,
} from './wallet/unified-wallet.js';
import type { Address } from 'viem';
import {
  loadPlayerStats,
  getRecentMatches,
  recordMatchResult,
  formatCentsAsDollars,
  formatRelativeTime,
  type MatchHistoryEntry,
} from './wallet/player-stats.js';
import {
  loadPracticeStats,
  recordPracticeMatch,
  getAverageScore,
  getWinRate,
} from './wallet/practice-stats.js';

/**
 * Mine or Die - Game Entry Point
 * Supports both offline single-player and online multiplayer
 */

// Landing page elements
const landingPage = document.getElementById('landing')!;
const gameContainer = document.getElementById('game-container')!;
const buttonsEl = document.getElementById('buttons')!;
const playButton = document.getElementById('play-btn')!;
const wtaButton = document.getElementById('wta-btn')!;
const practiceButton = document.getElementById('practice-btn')!;
const loadingEl = document.getElementById('loading')!;
const walletBtn = document.getElementById('wallet-btn')!;
const walletStatus = document.getElementById('wallet-status')!;

// Stats panel elements (Arena - right side)
const statsPanel = document.getElementById('stats-panel')!;
const statBalance = document.getElementById('stat-balance')!;
const statTotalPayouts = document.getElementById('stat-total-payouts')!;
const statUnclaimed = document.getElementById('stat-unclaimed')!;
const statGamesPlayed = document.getElementById('stat-games-played')!;
const recentMatchesEl = document.getElementById('recent-matches')!;
const claimBtn = document.getElementById('claim-btn') as HTMLButtonElement;

// Practice panel elements (left side)
const practicePanel = document.getElementById('practice-panel')!;
const practiceGamesPlayed = document.getElementById('practice-games-played')!;
const practiceWins = document.getElementById('practice-wins')!;
const practiceWinRate = document.getElementById('practice-win-rate')!;
const practiceHighScore = document.getElementById('practice-high-score')!;
const practiceAvgScore = document.getElementById('practice-avg-score')!;
const practiceBestRank = document.getElementById('practice-best-rank')!;

// Admin panel elements (bottom center - hidden unless admin wallet)
const adminPanel = document.getElementById('admin-panel')!;
const adminDevFees = document.getElementById('admin-dev-fees')!;
const adminContractBalance = document.getElementById('admin-contract-balance')!;
const adminTotalWagered = document.getElementById('admin-total-wagered')!;
const adminTotalGames = document.getElementById('admin-total-games')!;
const adminTotalPlayers = document.getElementById('admin-total-players')!;
const adminTotalPayouts = document.getElementById('admin-total-payouts')!;
const adminTotalOwed = document.getElementById('admin-total-owed')!;
const adminUniqueWinners = document.getElementById('admin-unique-winners')!;
const adminPendingClaims = document.getElementById('admin-pending-claims')!;
const adminLeaderboard = document.getElementById('admin-leaderboard')!;
const adminCloseBtn = document.getElementById('admin-close')!;
const adminRefreshBtn = document.getElementById('admin-refresh')!;

// Touch controls overlay
const touchControls = document.getElementById('touch-controls');

// Touch action buttons
const touchReadyBtn = document.getElementById('touch-ready-btn');
const touchFundBtn = document.getElementById('touch-fund-btn');
const touchBackBtn = document.getElementById('touch-back-btn');

// Guide toggle elements
const guideToggle = document.getElementById('guide-toggle');
const guideContent = document.getElementById('guide-content');

// Platform
const gameWindow = new GameWindow('game');
const input = new InputManager();
const renderer = new Renderer2D(gameWindow);
const gameLoop = new GameLoop();
const effects = new EffectsManager();
const audio = new AudioManager();

// Landing page state
let showingLanding = true;

// Game mode
type GameMode = 'menu' | 'offline' | 'online' | 'practice-lobby';
let gameMode: GameMode = 'menu';

// Offline state
let offlineWorld: World | null = null;
const offlineInputMap = new Map<PlayerId, InputState>();

// Practice lobby state (managed by server)
let practiceLobbyCountdown: number | null = null;
let practiceIsReady: boolean = false;

// Online state
let netClient: NetClient | null = null;
let onlineWorld: World | null = null;
let lobbyPlayers: LobbyPlayer[] = [];
let lobbyCountdown: number | null = null;
let matchResult: MatchResult | null = null;
let myPlayerId: PlayerId = 0;
let isReady: boolean = false;
let currentPayoutMode: PayoutMode = 'proportional';

// Pre-game countdown (5 second countdown before match starts)
let preGameCountdown: number = 0; // Countdown in seconds (0 = not active)
let preGameCountdownStartTime: number = 0; // When countdown started
const PRE_GAME_COUNTDOWN_DURATION = 5; // seconds

// Blockchain settlement state
let currentMatchId: string | null = null;
let currentEscrowAddress: Address | null = null;

// Staking state
let stakeStatus: 'none' | 'pending' | 'staking' | 'staked' | 'error' = 'none';
let hasStakedOnChain: boolean = false;

// Client prediction
let pendingInputs: Array<{ sequence: number; input: InputState }> = [];

// Event tracking for effects
let prevAsteroidIds = new Set<number>();
let prevAsteroidPositions = new Map<number, Vec2>();
let prevCrateCount = 0;
let prevProjectileCount = 0;
let prevTailLength = 0;
let prevScore = 0;
let wasThrusting = false;
// Ship state tracking for death/respawn effects
let prevShipAliveStates = new Map<number, boolean>();
let prevShipPositions = new Map<number, Vec2>();
let prevShipHp = new Map<number, number>();

// Animation time
let animationTime = 0;

/**
 * Format time as MM:SS
 */
function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Format score as dollars (for results screen where score = final payout in cents)
 */
function formatScore(cents: number): string {
  const dollars = cents / 100;
  return `$${dollars.toFixed(2)}`;
}

/**
 * Estimate payout using proportional distribution
 * Formula: (myScore / totalScore) × pool
 */
function estimatePayout(myScore: number, world: World): string {
  const totalScore = world.ships.reduce((sum, s) => sum + s.score, 0);
  if (totalScore === 0 || myScore === 0) return '$0.00';

  // Calculate pool: $1 per player, 5% dev fee
  const playerCount = world.ships.length;
  const poolCents = playerCount * 100 * 0.95;
  const estimatedCents = (myScore / totalScore) * poolCents;
  return `~$${(estimatedCents / 100).toFixed(2)}`;
}

/**
 * Get ship vertices for death explosion effect
 * Mirrors the renderer's getShipShape logic
 */
function getShipVertices(playerId: number, pos: Vec2, rot: number, size: number): Vec2[] {
  const shapeId = playerId % 8;

  switch (shapeId) {
    case 0: // Classic Arrow
      return [
        pos.add(Vec2.fromAngle(rot).scale(size * 1.1)),
        pos.add(Vec2.fromAngle(rot + Math.PI * 0.85).scale(size * 0.7)),
        pos.add(Vec2.fromAngle(rot + Math.PI).scale(size * 0.3)),
        pos.add(Vec2.fromAngle(rot - Math.PI * 0.85).scale(size * 0.7)),
      ];
    case 1: // Diamond
      return [
        pos.add(Vec2.fromAngle(rot).scale(size)),
        pos.add(Vec2.fromAngle(rot + Math.PI * 0.5).scale(size * 0.6)),
        pos.add(Vec2.fromAngle(rot + Math.PI).scale(size * 0.8)),
        pos.add(Vec2.fromAngle(rot - Math.PI * 0.5).scale(size * 0.6)),
      ];
    case 2: // Dart
      return [
        pos.add(Vec2.fromAngle(rot).scale(size * 1.2)),
        pos.add(Vec2.fromAngle(rot + Math.PI * 0.7).scale(size * 0.9)),
        pos.add(Vec2.fromAngle(rot + Math.PI * 0.9).scale(size * 0.4)),
        pos.add(Vec2.fromAngle(rot - Math.PI * 0.9).scale(size * 0.4)),
        pos.add(Vec2.fromAngle(rot - Math.PI * 0.7).scale(size * 0.9)),
      ];
    case 3: // Wedge
      return [
        pos.add(Vec2.fromAngle(rot).scale(size * 0.9)),
        pos.add(Vec2.fromAngle(rot + Math.PI * 0.6).scale(size * 0.85)),
        pos.add(Vec2.fromAngle(rot + Math.PI).scale(size * 0.7)),
        pos.add(Vec2.fromAngle(rot - Math.PI * 0.6).scale(size * 0.85)),
      ];
    case 4: // Needle
      return [
        pos.add(Vec2.fromAngle(rot).scale(size * 1.3)),
        pos.add(Vec2.fromAngle(rot + Math.PI * 0.8).scale(size * 0.5)),
        pos.add(Vec2.fromAngle(rot + Math.PI).scale(size * 0.6)),
        pos.add(Vec2.fromAngle(rot - Math.PI * 0.8).scale(size * 0.5)),
      ];
    case 5: // Chevron
      return [
        pos.add(Vec2.fromAngle(rot).scale(size)),
        pos.add(Vec2.fromAngle(rot + Math.PI * 0.65).scale(size * 0.95)),
        pos.add(Vec2.fromAngle(rot + Math.PI).scale(size * 0.2)),
        pos.add(Vec2.fromAngle(rot - Math.PI * 0.65).scale(size * 0.95)),
      ];
    case 6: // Hawk
      return [
        pos.add(Vec2.fromAngle(rot).scale(size * 1.1)),
        pos.add(Vec2.fromAngle(rot + Math.PI * 0.55).scale(size * 0.75)),
        pos.add(Vec2.fromAngle(rot + Math.PI * 0.8).scale(size * 0.9)),
        pos.add(Vec2.fromAngle(rot + Math.PI).scale(size * 0.35)),
        pos.add(Vec2.fromAngle(rot - Math.PI * 0.8).scale(size * 0.9)),
        pos.add(Vec2.fromAngle(rot - Math.PI * 0.55).scale(size * 0.75)),
      ];
    case 7: // Stealth
    default:
      return [
        pos.add(Vec2.fromAngle(rot).scale(size)),
        pos.add(Vec2.fromAngle(rot + Math.PI * 0.4).scale(size * 0.5)),
        pos.add(Vec2.fromAngle(rot + Math.PI * 0.75).scale(size * 0.85)),
        pos.add(Vec2.fromAngle(rot + Math.PI).scale(size * 0.5)),
        pos.add(Vec2.fromAngle(rot - Math.PI * 0.75).scale(size * 0.85)),
        pos.add(Vec2.fromAngle(rot - Math.PI * 0.4).scale(size * 0.5)),
      ];
  }
}

/**
 * Hydrate a world state received from server
 * Converts plain {x, y} objects back to Vec2 instances
 */
function hydrateWorld(world: World): World {
  // Hydrate ships
  for (const ship of world.ships) {
    ship.pos = new Vec2(ship.pos.x, ship.pos.y);
    ship.vel = new Vec2(ship.vel.x, ship.vel.y);
    for (const seg of ship.tailSegments) {
      seg.pos = new Vec2(seg.pos.x, seg.pos.y);
    }
  }

  // Hydrate asteroids
  for (const asteroid of world.asteroids) {
    asteroid.pos = new Vec2(asteroid.pos.x, asteroid.pos.y);
    asteroid.vel = new Vec2(asteroid.vel.x, asteroid.vel.y);
  }

  // Hydrate crates
  for (const crate of world.crates) {
    crate.pos = new Vec2(crate.pos.x, crate.pos.y);
    crate.vel = new Vec2(crate.vel.x, crate.vel.y);
  }

  // Hydrate projectiles
  for (const proj of world.projectiles) {
    proj.pos = new Vec2(proj.pos.x, proj.pos.y);
    proj.vel = new Vec2(proj.vel.x, proj.vel.y);
  }

  // Hydrate station
  world.station.pos = new Vec2(world.station.pos.x, world.station.pos.y);
  world.station.vel = new Vec2(world.station.vel.x, world.station.vel.y);
  world.station.targetPos = new Vec2(world.station.targetPos.x, world.station.targetPos.y);

  return world;
}

/**
 * Check for game events and trigger effects/audio
 */
function checkGameEvents(world: World, localInput: InputState): void {
  const localShip = world.ships.find((s) => s.playerId === myPlayerId);

  // Check for destroyed asteroids
  const currentAsteroidIds = new Set(world.asteroids.map(a => a.id));
  for (const prevId of prevAsteroidIds) {
    if (!currentAsteroidIds.has(prevId)) {
      // Asteroid was destroyed - spawn explosion at last known position
      const lastPos = prevAsteroidPositions.get(prevId);
      if (lastPos) {
        effects.spawnExplosion(lastPos, ASTEROID_OUTLINE_COLOR, 1.5);
        audio.playExplosion(1);
      }
    }
  }

  // Update asteroid tracking
  prevAsteroidIds = currentAsteroidIds;
  prevAsteroidPositions.clear();
  for (const asteroid of world.asteroids) {
    prevAsteroidPositions.set(asteroid.id, asteroid.pos.clone());
  }

  // Projectile fired
  if (world.projectiles.length > prevProjectileCount && localInput.shooting) {
    audio.playLaser();
  }
  prevProjectileCount = world.projectiles.length;

  // Crate picked up
  if (localShip && localShip.tailSegments.length > prevTailLength) {
    audio.playPickup();
    effects.spawnPickupEffect(localShip.pos, CRATE_COLOR);
  }
  prevTailLength = localShip?.tailSegments.length ?? 0;

  // Score increased (turn-in or kill happened)
  const currentScore = localShip?.score ?? 0;
  if (currentScore > prevScore) {
    const scoreDiff = currentScore - prevScore;
    // Turn-in: 1 pt per crate, or 11 pts per crate with bonus (5+ crates)
    // Kill: 5 pts
    if (scoreDiff > 0) {
      // Estimate crates turned in based on score diff
      // With bonus: 11 pts per crate, without: 1 pt per crate
      const cratesTurnedIn = scoreDiff >= 55 ? Math.floor(scoreDiff / 11) : scoreDiff;
      audio.playTurnIn(Math.max(1, cratesTurnedIn));
      if (localShip) {
        effects.spawnTurnInEffect(world.station.pos, STATION_COLOR, Math.max(1, cratesTurnedIn));
        // Show points popup
        effects.addScorePopup(localShip.pos, `+${scoreDiff} pts`, UI_ACCENT_COLOR);
      }
    }
  }
  prevScore = currentScore;

  // Check for ship deaths and respawns
  for (const ship of world.ships) {
    const wasAlive = prevShipAliveStates.get(ship.playerId);
    const prevPos = prevShipPositions.get(ship.playerId);
    const prevHp = prevShipHp.get(ship.playerId);
    const color = PLAYER_COLORS[ship.playerId % PLAYER_COLORS.length];

    // Ship just died (was alive, now dead)
    if (wasAlive === true && !ship.alive && prevPos) {
      // Get ship vertices for the death explosion
      const shipVertices = getShipVertices(ship.playerId, prevPos, ship.rot, SHIP_RADIUS);
      effects.spawnShipDeathExplosion(prevPos, color, shipVertices);
      audio.playExplosion(1.5);
    }

    // Ship just respawned (was dead or undefined, now alive with full HP)
    if ((wasAlive === false || wasAlive === undefined) && ship.alive && ship.hp === ship.maxHp) {
      effects.spawnRespawnFlash(ship.pos, color);
    }

    // Ship took damage (HP decreased but not dead)
    if (prevHp !== undefined && ship.hp < prevHp && ship.alive) {
      effects.spawnHitSparks(ship.pos, color);
      audio.playExplosion(0.3);
    }

    // Update tracking
    prevShipAliveStates.set(ship.playerId, ship.alive);
    prevShipPositions.set(ship.playerId, ship.pos.clone());
    prevShipHp.set(ship.playerId, ship.hp);
  }

  // Engine thrust trail
  if (localShip && localInput.thrust) {
    if (!wasThrusting) {
      audio.startThrust();
    }
    // Calculate engine position (back of ship)
    const engineOffset = Vec2.fromAngle(localShip.rot + Math.PI).scale(SHIP_RADIUS * 0.8);
    const enginePos = localShip.pos.add(engineOffset);
    const color = PLAYER_COLORS[localShip.playerId % PLAYER_COLORS.length];
    effects.spawnEngineTrail(enginePos, localShip.vel, color);
    wasThrusting = true;
  } else if (wasThrusting) {
    audio.stopThrust();
    wasThrusting = false;
  }
}

/**
 * Start practice lobby (connects to server for multiplayer practice with NPCs)
 */
function startPracticeLobby(): void {
  gameMode = 'practice-lobby';
  practiceLobbyCountdown = null;
  practiceIsReady = false;
  onlineWorld = null;
  lobbyPlayers = [];
  matchResult = null;
  pendingInputs = [];
  resetEventTracking();
  audio.init();

  // Connect to server for practice mode
  const isProduction = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';
  const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const serverUrl = isProduction
    ? `${wsProtocol}//${window.location.host}`
    : `ws://${window.location.hostname}:8080`;

  netClient = new NetClient(serverUrl, {
    onConnect: () => {
      console.log('Connected to practice server');
      const playerName = `Player_${Math.floor(Math.random() * 1000)}`;
      netClient!.joinPractice(playerName);
    },
    onDisconnect: () => {
      console.log('Disconnected from practice server');
      // Fall back to local mode if server unavailable
      if (gameMode === 'practice-lobby') {
        console.log('Server unavailable, starting local practice...');
        startOfflineGame();
      }
    },
    onError: (error) => {
      console.error('Practice server error:', error);
    },
    onLobbyUpdate: (players, myId, countdown) => {
      console.log('Practice lobby update: players:', players.length, 'myId:', myId, 'countdown:', countdown);
      // Play countdown beep
      if (countdown !== null && countdown !== practiceLobbyCountdown && countdown > 0) {
        audio.playCountdown(countdown === 1);
      }
      lobbyPlayers = players;
      myPlayerId = myId;
      practiceLobbyCountdown = countdown;

      // Check if any real player is ready
      const myPlayer = players.find(p => p.playerId === myId);
      if (myPlayer) {
        practiceIsReady = myPlayer.ready;
      }
    },
    onMatchStart: (world) => {
      console.log('Practice match started! Ships:', world.ships.length, 'gameMode before:', gameMode);
      gameMode = 'offline'; // Use offline rendering but with server state
      onlineWorld = hydrateWorld(world);
      pendingInputs = [];
      matchResult = null;
      resetEventTracking();

      // Start pre-game countdown for practice too
      preGameCountdown = PRE_GAME_COUNTDOWN_DURATION;
      preGameCountdownStartTime = performance.now();

      console.log('Practice match setup complete. gameMode:', gameMode, 'onlineWorld set:', !!onlineWorld);
    },
    onStateSnapshot: (tick, lastProcessedInput, state) => {
      handlePracticeSnapshot(tick, lastProcessedInput, hydrateWorld(state));
    },
    onMatchEnd: (result) => {
      console.log('Practice match ended!', result);
      matchResult = result;
      gameMode = 'practice-lobby';

      // Record practice stats only (no wallet/arena stats for practice mode)
      const myRanking = result.rankings.find((r) => r.playerId === myPlayerId);
      if (myRanking) {
        const playerCount = result.rankings.length;
        recordPracticeMatch(myRanking.rank, playerCount, myRanking.score);
        updatePracticePanel();
      }
      // Note: Practice mode does NOT record to arena stats or payouts
    },
  });

  netClient.connect();
  console.log('Connecting to practice server...');
}

/**
 * Start the actual offline game (from lobby)
 */
function startOfflineGame(): void {
  gameMode = 'offline';
  offlineWorld = createWorld(1, 15, NPC_COUNT);
  myPlayerId = 0;
  resetEventTracking();
  audio.playCountdown(true); // Final beep
  console.log('Started offline game with', NPC_COUNT, 'NPCs');
}

/**
 * Start offline single-player mode directly (skip lobby)
 */
function startOfflineMode(): void {
  gameMode = 'offline';
  offlineWorld = createWorld(1, 15, NPC_COUNT);
  myPlayerId = 0;
  resetEventTracking();
  audio.init();
  console.log('Started offline mode with', NPC_COUNT, 'NPCs');
}

/**
 * Start online multiplayer mode
 * @param payoutMode - 'proportional' (default) or 'winner-takes-all'
 */
function startOnlineMode(payoutMode: PayoutMode = 'proportional'): void {
  gameMode = 'online';
  onlineWorld = null;
  lobbyPlayers = [];
  lobbyCountdown = null;
  matchResult = null;
  isReady = false;
  pendingInputs = [];
  currentPayoutMode = payoutMode;
  // Reset blockchain state
  currentMatchId = null;
  currentEscrowAddress = null;
  stakeStatus = 'none';
  hasStakedOnChain = false;
  resetEventTracking();
  audio.init();

  // Use same host/port in production (Railway), port 8080 for local dev
  const isProduction = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';
  const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const serverUrl = isProduction
    ? `${wsProtocol}//${window.location.host}`
    : `ws://${window.location.hostname}:8080`;

  netClient = new NetClient(serverUrl, {
    onConnect: () => {
      console.log('Connected to server');
      const playerName = `Player_${Math.floor(Math.random() * 1000)}`;
      const walletAddr = getWalletAddress();
      // Join the appropriate room type based on payout mode
      if (payoutMode === 'winner-takes-all') {
        netClient!.joinWinnerTakesAll(playerName, walletAddr ?? undefined);
      } else {
        netClient!.joinLobby(playerName, walletAddr ?? undefined);
      }
    },
    onDisconnect: () => {
      console.log('Disconnected from server');
    },
    onError: (error) => {
      console.error('Network error:', error);
    },
    onLobbyUpdate: (players, myId, countdown, matchId, escrowAddress, payoutMode) => {
      // Play countdown beep
      if (countdown !== null && countdown !== lobbyCountdown && countdown > 0) {
        audio.playCountdown(countdown === 1);
      }
      lobbyPlayers = players;
      myPlayerId = myId;
      lobbyCountdown = countdown;

      // Track payout mode
      if (payoutMode) {
        currentPayoutMode = payoutMode;
      }

      // Update escrow info when countdown starts
      if (matchId && escrowAddress) {
        currentMatchId = matchId;
        const newEscrow = escrowAddress as Address;

        // If escrow changed, reset staking status
        if (currentEscrowAddress !== newEscrow) {
          currentEscrowAddress = newEscrow;
          stakeStatus = 'pending';
          hasStakedOnChain = false;
          console.log('Escrow created:', escrowAddress, 'matchId:', matchId);
        }
      }
    },
    onMatchStart: (world, matchId, escrowAddress) => {
      console.log('Match starting with pre-game countdown!', { matchId, escrowAddress });
      onlineWorld = hydrateWorld(world);
      pendingInputs = [];
      matchResult = null;
      // Store blockchain info
      currentMatchId = matchId ?? null;
      currentEscrowAddress = (escrowAddress as Address) ?? null;
      resetEventTracking();

      // Start pre-game countdown
      preGameCountdown = PRE_GAME_COUNTDOWN_DURATION;
      preGameCountdownStartTime = performance.now();
    },
    onStateSnapshot: (tick, lastProcessedInput, state) => {
      handleServerSnapshot(tick, lastProcessedInput, hydrateWorld(state));
    },
    onMatchEnd: (result) => {
      console.log('Match ended!', result);
      matchResult = result;

      // Record match result for stats
      if (isWalletConnected()) {
        const address = getWalletAddress();
        if (address) {
          const myRanking = result.rankings.find((r) => r.playerId === myPlayerId);
          if (myRanking) {
            // Use actual match ID if available, otherwise generate one
            const matchId = currentMatchId || `match-${Date.now()}`;
            // Estimate payout based on proportional distribution
            const totalScore = result.rankings.reduce((sum, r) => sum + r.score, 0);
            const playerCount = result.rankings.length;
            const poolCents = playerCount * 100 * 0.95; // $1 entry, 5% dev fee
            const payoutCents = totalScore > 0
              ? Math.floor((myRanking.score / totalScore) * poolCents)
              : Math.floor(poolCents / playerCount);

            recordMatchResult(
              address,
              matchId,
              myRanking.rank,
              playerCount,
              myRanking.score,
              payoutCents,
              100 // $1 entry fee
            );
          }
        }
      }
    },
    onSettlement: (settlement) => {
      console.log('Settlement received:', settlement);
      // Settlement data available for future claiming via stats panel
    },
  });

  netClient.connect();
}

/**
 * Handle server state snapshot with client-side prediction reconciliation
 */
function handleServerSnapshot(
  tick: number,
  lastProcessedInput: number[],
  serverState: World
): void {
  // Find my ship's last processed input sequence
  const myShipIndex = serverState.ships.findIndex((s) => s.playerId === myPlayerId);
  const myLastProcessed = myShipIndex >= 0 ? lastProcessedInput[myShipIndex] : 0;

  // Remove acknowledged inputs
  pendingInputs = pendingInputs.filter((pi) => pi.sequence > myLastProcessed);

  // Start from server state
  onlineWorld = serverState;

  // Re-apply unacknowledged inputs (client-side prediction reconciliation)
  if (onlineWorld && pendingInputs.length > 0) {
    const localInputMap = new Map<PlayerId, InputState>();

    for (const pending of pendingInputs) {
      localInputMap.set(myPlayerId, pending.input);
      // Re-run simulation for prediction
      runSystems(onlineWorld, localInputMap, FIXED_DT);
    }
  }
}

/**
 * Handle practice mode server snapshot (similar to online but uses onlineWorld)
 */
function handlePracticeSnapshot(
  tick: number,
  lastProcessedInput: number[],
  serverState: World
): void {
  // Find my ship's last processed input sequence
  const myShipIndex = serverState.ships.findIndex((s) => s.playerId === myPlayerId);
  const myLastProcessed = myShipIndex >= 0 ? lastProcessedInput[myShipIndex] : 0;

  // Remove acknowledged inputs
  pendingInputs = pendingInputs.filter((pi) => pi.sequence > myLastProcessed);

  // Start from server state
  onlineWorld = serverState;

  // Re-apply unacknowledged inputs (client-side prediction reconciliation)
  if (onlineWorld && pendingInputs.length > 0) {
    const localInputMap = new Map<PlayerId, InputState>();

    for (const pending of pendingInputs) {
      localInputMap.set(myPlayerId, pending.input);
      runSystems(onlineWorld, localInputMap, FIXED_DT);
    }
  }
}

/**
 * Fixed update - physics and game logic
 */
function fixedUpdate(dt: number): void {
  // Get local input
  const localInput = input.getInputState();

  // Update effects and animation time
  effects.update(dt);
  animationTime += dt;

  // Practice lobby - server handles countdown, we just render
  // (practiceLobbyCountdown is updated via onLobbyUpdate callback)

  // Practice mode with server - send inputs and apply prediction
  if (gameMode === 'offline' && netClient && onlineWorld) {
    // Update pre-game countdown for practice mode
    if (preGameCountdown > 0) {
      const elapsed = (performance.now() - preGameCountdownStartTime) / 1000;
      const newCountdown = Math.ceil(PRE_GAME_COUNTDOWN_DURATION - elapsed);

      // Play countdown beep when countdown changes
      if (newCountdown !== preGameCountdown && newCountdown > 0) {
        audio.playCountdown(newCountdown === 1);
      }

      preGameCountdown = Math.max(0, newCountdown);

      // Play final beep when countdown ends
      if (preGameCountdown === 0) {
        audio.playCountdown(true);
      }

      // Don't process input during countdown
      return;
    }

    // Connected to practice server - send input
    const sequence = netClient.sendInput(localInput);
    pendingInputs.push({ sequence, input: localInput });

    // Apply input locally for prediction
    const localInputMap = new Map<PlayerId, InputState>();
    localInputMap.set(myPlayerId, localInput);
    runSystems(onlineWorld, localInputMap, dt);

    // Check for game events
    checkGameEvents(onlineWorld, localInput);

    // Update ship position for mouse aim
    const localShip = onlineWorld.ships.find((s) => s.playerId === myPlayerId);
    if (localShip) {
      input.setShipPosition(localShip.pos.x, localShip.pos.y);
    }
  } else if (gameMode === 'offline' && offlineWorld) {
    // Offline mode - run full simulation locally
    offlineInputMap.set(myPlayerId, localInput);

    // Generate NPC inputs
    const npcInputs = generateNpcInputs(offlineWorld, dt);
    for (const [npcId, npcInput] of npcInputs) {
      offlineInputMap.set(npcId, npcInput);
    }

    const matchEnded = runSystems(offlineWorld, offlineInputMap, dt);

    // Check for game events and trigger effects
    checkGameEvents(offlineWorld, localInput);

    if (matchEnded) {
      console.log('Match ended! Restarting...');
      offlineWorld = createWorld(1, 15, NPC_COUNT);
      resetEventTracking();
    }

    // Update input manager with ship position
    const localShip = offlineWorld.ships.find((s) => s.playerId === myPlayerId);
    if (localShip) {
      input.setShipPosition(localShip.pos.x, localShip.pos.y);
    }
  } else if (gameMode === 'online' && netClient) {
    // Online mode - send input to server, apply prediction
    if (netClient.gameState === 'playing' && onlineWorld) {
      // Update pre-game countdown
      if (preGameCountdown > 0) {
        const elapsed = (performance.now() - preGameCountdownStartTime) / 1000;
        const newCountdown = Math.ceil(PRE_GAME_COUNTDOWN_DURATION - elapsed);

        // Play countdown beep when countdown changes
        if (newCountdown !== preGameCountdown && newCountdown > 0) {
          audio.playCountdown(newCountdown === 1);
        }

        preGameCountdown = Math.max(0, newCountdown);

        // Play final beep when countdown ends
        if (preGameCountdown === 0) {
          audio.playCountdown(true);
        }

        // Don't process input during countdown
        return;
      }

      // Send input and store for reconciliation
      const sequence = netClient.sendInput(localInput);
      pendingInputs.push({ sequence, input: localInput });

      // Apply input locally for immediate feedback (prediction)
      const localInputMap = new Map<PlayerId, InputState>();
      localInputMap.set(myPlayerId, localInput);

      // Run prediction
      runSystems(onlineWorld, localInputMap, dt);

      // Check for game events and trigger effects
      checkGameEvents(onlineWorld, localInput);

      // Update input manager with predicted ship position
      const localShip = onlineWorld.ships.find((s) => s.playerId === myPlayerId);
      if (localShip) {
        input.setShipPosition(localShip.pos.x, localShip.pos.y);
      }
    }
  }
}

/**
 * Reset event tracking (on match start/restart)
 */
function resetEventTracking(): void {
  prevAsteroidIds = new Set();
  prevAsteroidPositions = new Map();
  prevCrateCount = 0;
  prevProjectileCount = 0;
  prevTailLength = 0;
  prevScore = 0;
  wasThrusting = false;
  prevShipAliveStates = new Map();
  prevShipPositions = new Map();
  prevShipHp = new Map();
  effects.clear();
}

/**
 * Render - draw everything
 */
function render(alpha: number): void {
  // Update touch buttons every frame to reflect state changes
  updateTouchButtons();

  // Don't render game when showing landing page
  if (showingLanding) return;

  renderer.beginFrame();

  if (gameMode === 'menu') {
    renderMenu();
  } else if (gameMode === 'practice-lobby') {
    renderPracticeLobby();
  } else if (gameMode === 'offline') {
    // Offline can be local (offlineWorld) or server-connected practice (onlineWorld)
    const worldToRender = onlineWorld || offlineWorld;
    renderGame(worldToRender);
    // Apply bloom post-processing for game view
    gameWindow.applyBloom();

    // Render pre-game countdown overlay for practice mode
    if (preGameCountdown > 0 && netClient) {
      renderPreGameCountdown();
    }
  } else if (gameMode === 'online') {
    if (netClient?.gameState === 'lobby' || netClient?.gameState === 'countdown') {
      renderLobby();
    } else if (netClient?.gameState === 'playing') {
      renderGame(onlineWorld);
      // Apply bloom post-processing for game view
      gameWindow.applyBloom();

      // Render pre-game countdown overlay
      if (preGameCountdown > 0) {
        renderPreGameCountdown();
      }
    } else if (netClient?.gameState === 'results') {
      renderResults();
    } else {
      renderConnecting();
    }
  }
}

/**
 * Render pre-game countdown overlay (big retro numbers)
 */
function renderPreGameCountdown(): void {
  const centerX = gameWindow.width / 2;
  const centerY = gameWindow.height / 2;

  // Semi-transparent dark overlay
  renderer.drawRectFilled(
    new Vec2(0, 0),
    gameWindow.width,
    gameWindow.height,
    'rgba(0, 0, 0, 0.7)',
    false
  );

  // Calculate pulse effect based on time
  const elapsed = (performance.now() - preGameCountdownStartTime) / 1000;
  const fraction = elapsed % 1; // 0-1 within each second
  const pulse = 1 + Math.sin(fraction * Math.PI) * 0.15; // Pulse between 1.0 and 1.15

  // Big countdown number with retro style
  const countdownNum = preGameCountdown.toString();
  const fontSize = Math.floor(200 * pulse);

  // Glow effect - draw multiple times with decreasing opacity
  const glowColors = [
    'rgba(0, 245, 255, 0.1)',
    'rgba(0, 245, 255, 0.2)',
    'rgba(0, 245, 255, 0.4)',
  ];

  for (let i = 0; i < glowColors.length; i++) {
    const glowSize = fontSize + (glowColors.length - i) * 20;
    renderer.drawText(countdownNum, new Vec2(centerX, centerY), glowColors[i], glowSize, 'center', true);
  }

  // Main number
  renderer.drawText(countdownNum, new Vec2(centerX, centerY), UI_ACCENT_COLOR, fontSize, 'center', true);

  // "GET READY" text above
  renderer.drawText('GET READY', new Vec2(centerX, centerY - 150), UI_TEXT_COLOR, 36, 'center', true);

  // Instructions below
  renderer.drawText('WASD to move • Mouse to aim • Click to shoot', new Vec2(centerX, centerY + 150), '#666666', 18, 'center', false);
}

/**
 * Render main menu
 */
function renderMenu(): void {
  const centerX = gameWindow.width / 2;
  const centerY = gameWindow.height / 2;

  renderer.drawText('MINE OR DIE', new Vec2(centerX, centerY - 100), UI_ACCENT_COLOR, 48, 'center', true);
  renderer.drawText('Press 1 for Offline Mode', new Vec2(centerX, centerY), UI_TEXT_COLOR, 20, 'center', false);
  renderer.drawText('Press 2 for Online Multiplayer', new Vec2(centerX, centerY + 30), UI_TEXT_COLOR, 20, 'center', false);
  renderer.drawText('(Start server first: pnpm dev:server)', new Vec2(centerX, centerY + 70), '#666666', 14, 'center', false);
}

/**
 * Render practice lobby screen
 */
function renderPracticeLobby(): void {
  const centerX = gameWindow.width / 2;
  let y = 100;

  renderer.drawText('PRACTICE ARENA', new Vec2(centerX, y), UI_ACCENT_COLOR, 36, 'center', true);
  y += 50;

  // Show waiting or countdown status
  if (practiceLobbyCountdown !== null && practiceLobbyCountdown > 0) {
    renderer.drawText(`Starting in ${practiceLobbyCountdown}...`, new Vec2(centerX, y), STATION_COLOR, 28, 'center', true);
  } else if (netClient) {
    renderer.drawText('Waiting for players...', new Vec2(centerX, y), UI_TEXT_COLOR, 20, 'center', false);
  } else {
    renderer.drawText('Connecting to server...', new Vec2(centerX, y), UI_TEXT_COLOR, 20, 'center', false);
  }
  y += 50;

  // Players section - show from server lobby data
  renderer.drawText(`Players (${MAX_PLAYERS} slots):`, new Vec2(centerX, y), UI_TEXT_COLOR, 18, 'center', false);
  y += 30;

  if (lobbyPlayers.length > 0) {
    // Show players from server
    for (const player of lobbyPlayers) {
      const color = PLAYER_COLORS[player.playerId % PLAYER_COLORS.length];
      const isMe = player.playerId === myPlayerId;
      const isBot = player.name.startsWith('Bot ');
      const readyText = player.ready ? ' [READY]' : '';
      const meText = isMe ? ' (you)' : '';

      if (isBot) {
        renderer.drawText(`${player.name}`, new Vec2(centerX, y), color + '80', 14, 'center', false);
      } else {
        renderer.drawText(`${player.name}${meText}${readyText}`, new Vec2(centerX, y), color, 16, 'center', false);
      }
      y += isBot ? 20 : 25;
    }
  } else {
    // Show placeholder while connecting
    renderer.drawText('Connecting...', new Vec2(centerX, y), '#666666', 14, 'center', false);
  }

  // Instructions
  y = gameWindow.height - 100;
  if (practiceIsReady) {
    renderer.drawText('Press R to cancel ready', new Vec2(centerX, y), '#888888', 14, 'center', false);
  } else {
    renderer.drawText('Press R when ready to start countdown', new Vec2(centerX, y), STATION_COLOR, 16, 'center', false);
  }
  renderer.drawText('Other players can join and replace bots', new Vec2(centerX, y + 25), '#666666', 12, 'center', false);
  renderer.drawText('Press ESC to return to menu', new Vec2(centerX, y + 45), '#666666', 12, 'center', false);
}

/**
 * Render lobby screen
 */
function renderLobby(): void {
  const centerX = gameWindow.width / 2;
  let y = 100;

  // Show lobby title with payout mode
  const modeText = currentPayoutMode === 'winner-takes-all' ? 'WINNER TAKES ALL' : 'PROPORTIONAL';
  const modeColor = currentPayoutMode === 'winner-takes-all' ? '#ff6600' : UI_ACCENT_COLOR;
  renderer.drawText('LOBBY', new Vec2(centerX, y), UI_ACCENT_COLOR, 36, 'center', true);
  y += 35;
  renderer.drawText(modeText, new Vec2(centerX, y), modeColor, 16, 'center', true);
  y += 35;

  // Countdown
  if (lobbyCountdown !== null) {
    renderer.drawText(`Starting in ${lobbyCountdown}...`, new Vec2(centerX, y), STATION_COLOR, 24, 'center', true);
    y += 40;

    // Show staking prompt during countdown if escrow is available
    if (currentEscrowAddress && isWalletConnected()) {
      if (stakeStatus === 'pending') {
        renderer.drawText('Press S to stake $1 USDC', new Vec2(centerX, y), '#ffaa00', 18, 'center', true);
        y += 25;
      } else if (stakeStatus === 'staking') {
        renderer.drawText('Staking...', new Vec2(centerX, y), UI_TEXT_COLOR, 16, 'center', false);
        y += 25;
      } else if (stakeStatus === 'staked') {
        renderer.drawText('Staked! Ready to play', new Vec2(centerX, y), STATION_COLOR, 16, 'center', false);
        y += 25;
      } else if (stakeStatus === 'error') {
        renderer.drawText('Stake failed - Press S to retry', new Vec2(centerX, y), '#ff3333', 16, 'center', false);
        y += 25;
      }
    } else if (currentEscrowAddress && !isWalletConnected()) {
      renderer.drawText('Connect wallet to stake', new Vec2(centerX, y), '#888888', 14, 'center', false);
      y += 25;
    }
  }

  // Players
  renderer.drawText('Players:', new Vec2(centerX, y), UI_TEXT_COLOR, 18, 'center', false);
  y += 30;

  for (const player of lobbyPlayers) {
    const color = PLAYER_COLORS[player.playerId % PLAYER_COLORS.length];
    const readyText = player.ready ? ' [READY]' : '';
    const meText = player.playerId === myPlayerId ? ' (you)' : '';
    renderer.drawText(
      `${player.name}${meText}${readyText}`,
      new Vec2(centerX, y),
      color,
      16,
      'center',
      false
    );
    y += 25;
  }

  // Instructions
  y = gameWindow.height - 80;
  const myReady = lobbyPlayers.find((p) => p.playerId === myPlayerId)?.ready;
  if (myReady) {
    renderer.drawText('Press R to unready', new Vec2(centerX, y), '#888888', 14, 'center', false);
  } else {
    renderer.drawText('Press R when ready', new Vec2(centerX, y), STATION_COLOR, 16, 'center', false);
  }
  renderer.drawText('Need 2+ players ready to start', new Vec2(centerX, y + 25), '#666666', 12, 'center', false);
  renderer.drawText('Press ESC to return to menu', new Vec2(centerX, y + 45), '#666666', 12, 'center', false);
}

/**
 * Render connecting screen
 */
function renderConnecting(): void {
  const centerX = gameWindow.width / 2;
  const centerY = gameWindow.height / 2;

  renderer.drawText('Connecting...', new Vec2(centerX, centerY), UI_TEXT_COLOR, 24, 'center', false);
  renderer.drawText('Press ESC to cancel', new Vec2(centerX, centerY + 40), '#666666', 14, 'center', false);
}

/**
 * Render match results
 */
function renderResults(): void {
  const centerX = gameWindow.width / 2;
  let y = 100;

  renderer.drawText('MATCH OVER', new Vec2(centerX, y), UI_ACCENT_COLOR, 36, 'center', true);
  y += 60;

  if (matchResult) {
    for (const ranking of matchResult.rankings) {
      const color = PLAYER_COLORS[ranking.playerId % PLAYER_COLORS.length];
      const meText = ranking.playerId === myPlayerId ? ' (you)' : '';
      renderer.drawText(
        `#${ranking.rank}: ${formatScore(ranking.score)} - Kills ${ranking.kills} - Ore ${ranking.oreTurnedIn}${meText}`,
        new Vec2(centerX, y),
        color,
        18,
        'center',
        false
      );
      y += 30;
    }
  }

  y = gameWindow.height - 60;
  renderer.drawText('Returning to lobby...', new Vec2(centerX, y), '#888888', 14, 'center', false);
}

/**
 * Render the game world
 */
function renderGame(world: World | null): void {
  if (!world) return;

  renderer.beginWorld();

  // Apply screen shake
  const shake = effects.getShakeOffset();
  if (shake.x !== 0 || shake.y !== 0) {
    renderer.applyShake(shake.x, shake.y);
  }

  // Draw grid
  renderer.drawGrid();

  // Draw arena border
  renderer.drawRect(Vec2.zero(), ARENA_WIDTH, ARENA_HEIGHT, '#224466', 2, true);

  // Draw station turn-in radius with animated pulse
  const stationPulse = 0.9 + 0.1 * Math.sin(animationTime * 2);
  const radiusPulse = 0.95 + 0.05 * Math.sin(animationTime * 3);
  renderer.drawCircle(world.station.pos, STATION_TURN_IN_RADIUS * radiusPulse, STATION_COLOR + '30', 1, true);

  // Draw station with pulsing animation
  renderer.drawStation(world.station.pos, STATION_RADIUS * stationPulse, STATION_COLOR, STATION_OUTLINE_COLOR);

  // Draw asteroids
  for (const asteroid of world.asteroids) {
    renderer.drawAsteroid(
      asteroid.pos,
      asteroid.radius,
      asteroid.rot,
      ASTEROID_COLOR,
      ASTEROID_OUTLINE_COLOR,
      asteroid.id
    );

    if (asteroid.hp < asteroid.maxHp) {
      const barWidth = asteroid.radius * 2;
      const barHeight = 4;
      const healthPct = asteroid.hp / asteroid.maxHp;
      const barPos = new Vec2(asteroid.pos.x - barWidth / 2, asteroid.pos.y - asteroid.radius - 10);
      renderer.drawRectFilled(barPos, barWidth, barHeight, '#333333', false);
      renderer.drawRectFilled(barPos, barWidth * healthPct, barHeight, '#00ff00', false);
    }
  }

  // Draw crates with pulsing glow
  for (const crate of world.crates) {
    const pulse = 0.8 + 0.2 * Math.sin(animationTime * 4 + crate.id * 0.5);
    const baseColor = crate.pickupCooldown > 0 ? CRATE_COLOR + '80' : CRATE_COLOR;
    const outline = crate.pickupCooldown > 0 ? CRATE_OUTLINE_COLOR + '80' : CRATE_OUTLINE_COLOR;
    const size = CRATE_SIZE * pulse;
    renderer.drawCrate(crate.pos, size, baseColor, outline);
  }

  // Draw projectiles
  for (const proj of world.projectiles) {
    const color = PLAYER_COLORS[proj.ownerId % PLAYER_COLORS.length];
    renderer.drawProjectile(proj.pos, proj.vel, color);
  }

  // Draw ships
  for (const ship of world.ships) {
    if (!ship.alive) continue;

    const color = PLAYER_COLORS[ship.playerId % PLAYER_COLORS.length];
    const isLocalPlayer = ship.playerId === myPlayerId;

    // Draw tail
    for (let i = ship.tailSegments.length - 1; i >= 0; i--) {
      const segment = ship.tailSegments[i];
      renderer.drawCrate(segment.pos, CRATE_SIZE * 0.9, CRATE_COLOR, color);
    }

    // Draw tail connections
    if (ship.tailSegments.length > 0) {
      let prevPos = ship.pos;
      for (const segment of ship.tailSegments) {
        renderer.drawLine(prevPos, segment.pos, color + '60', 2, false);
        prevPos = segment.pos;
      }
    }

    // Draw ship with unique shape per player (with damage expansion)
    renderer.drawShip(ship.pos, ship.rot, SHIP_RADIUS, color, ship.playerId, undefined, ship.damageLevel || 0);

    // Draw player name for other players
    if (!isLocalPlayer && gameMode === 'online') {
      const player = lobbyPlayers.find((p) => p.playerId === ship.playerId);
      if (player) {
        renderer.drawText(
          player.name,
          new Vec2(ship.pos.x, ship.pos.y - SHIP_RADIUS - 15),
          color,
          10,
          'center',
          false
        );
      }
    }
  }

  // Draw particles
  for (const particle of effects.getParticles()) {
    const alpha = particle.life / particle.maxLife;
    renderer.drawParticle(
      particle.pos,
      particle.size,
      particle.color,
      particle.type,
      alpha,
      particle.rotation,
      particle.endOffset
    );
  }

  // Draw score popups
  for (const popup of effects.getPopups()) {
    const alpha = popup.life / popup.maxLife;
    renderer.drawPopup(popup.pos, popup.text, popup.color, alpha);
  }

  renderer.endWorld();

  // Draw UI
  const localShip = world.ships.find((s) => s.playerId === myPlayerId);

  // Timer
  renderer.drawText(formatTime(world.timeRemaining), new Vec2(gameWindow.width / 2, 20), UI_TEXT_COLOR, 24, 'center', true);

  // Score display: points + estimated payout (proportional distribution)
  if (localShip) {
    renderer.drawText(`Score: ${localShip.score} pts`, new Vec2(gameWindow.width / 2, 50), UI_ACCENT_COLOR, 18, 'center', true);
    renderer.drawText(`Est. Payout: ${estimatePayout(localShip.score, world)}`, new Vec2(gameWindow.width / 2, 75), UI_TEXT_COLOR, 14, 'center', false);
    const bonusText = localShip.tailSegments.length >= 5 ? ' (BONUS!)' : '';
    renderer.drawText(`Ore: ${localShip.tailSegments.length}${bonusText}`, new Vec2(gameWindow.width / 2, 95), CRATE_COLOR, 14, 'center', false);
  }

  // Leaderboard (online mode or practice with NPCs)
  if (world.ships.length > 1) {
    const sorted = [...world.ships].sort((a, b) => b.score - a.score);
    let y = 100;
    renderer.drawText('Leaderboard', new Vec2(gameWindow.width - 120, y), UI_TEXT_COLOR, 14, 'center', false);
    y += 20;

    for (let i = 0; i < Math.min(sorted.length, 5); i++) {
      const ship = sorted[i];
      const color = PLAYER_COLORS[ship.playerId % PLAYER_COLORS.length];
      const isNpc = ship.isNpc;
      const name = isNpc
        ? `Bot ${ship.playerId}`
        : lobbyPlayers.find((p) => p.playerId === ship.playerId)?.name || `P${ship.playerId}`;
      const meText = ship.playerId === myPlayerId ? '*' : '';
      const estPayout = estimatePayout(ship.score, world);
      renderer.drawText(`${meText}${name}: ${ship.score} (${estPayout})`, new Vec2(gameWindow.width - 120, y), color, 12, 'center', false);
      y += 18;
    }
  }

  // Stats (top left)
  const fps = gameLoop.getFps();
  renderer.drawText(`FPS: ${fps}`, new Vec2(10, 10), '#668899', 12, 'left', false);

  if (gameMode === 'online') {
    renderer.drawText(`Pending: ${pendingInputs.length}`, new Vec2(10, 26), '#668899', 12, 'left', false);
  }

  // Controls (bottom left)
  const helpY = gameWindow.height - 50;
  renderer.drawText('WASD - Move | SPACE - Shoot', new Vec2(10, helpY), '#445566', 12, 'left', false);
  renderer.drawText('ESC - Menu', new Vec2(10, helpY + 16), '#445566', 12, 'left', false);
}

/**
 * Frame start - handle input
 */
function frameStart(): void {
  // Update mouse world position
  const mousePos = input.getMousePosition();
  const worldPos = gameWindow.screenToWorld(mousePos.x, mousePos.y);
  input.setMouseWorldPosition(worldPos.x, worldPos.y);

  // Update touch joystick visuals
  input.updateJoystickVisuals();
}

/**
 * Handle staking to escrow during countdown
 */
async function handleStakeToEscrow(): Promise<void> {
  if (!currentEscrowAddress || !currentMatchId || !isWalletConnected()) {
    console.log('Cannot stake: missing escrow, matchId, or wallet');
    return;
  }

  if (stakeStatus === 'staking' || stakeStatus === 'staked') {
    console.log('Already staking or staked');
    return;
  }

  const walletAddr = getWalletAddress();
  if (!walletAddr) {
    console.log('Cannot stake: no wallet address');
    return;
  }

  stakeStatus = 'staking';
  audio.playClick();

  try {
    console.log('Staking to escrow:', currentEscrowAddress);

    const txHash = await stakeToEscrow(currentEscrowAddress);
    console.log('Stake successful! TX:', txHash);

    stakeStatus = 'staked';
    hasStakedOnChain = true;
    audio.playTurnIn(3); // Play a confirmation sound

    // Notify server of successful stake
    if (netClient) {
      netClient.sendStakeConfirmation(currentMatchId, walletAddr, txHash);
    }
  } catch (err) {
    console.error('Stake failed:', err);
    stakeStatus = 'error';
  }
}

/**
 * Handle keyboard shortcuts
 */
function setupKeyboardShortcuts(): void {
  window.addEventListener('keydown', (e) => {
    if (gameMode === 'menu') {
      if (e.key === '1') {
        audio.playClick();
        startOfflineMode();
      } else if (e.key === '2') {
        audio.playClick();
        startOnlineMode();
      }
    } else if (gameMode === 'practice-lobby') {
      if (e.key === 'r' || e.key === 'R') {
        // Toggle ready state in practice lobby - send to server
        console.log('R pressed in practice-lobby. countdown:', practiceLobbyCountdown, 'netClient:', !!netClient);
        if (netClient) {
          audio.playClick();
          practiceIsReady = !practiceIsReady;
          netClient.setReady(practiceIsReady);
          console.log('Sent ready state:', practiceIsReady);
        }
      }
    } else if (gameMode === 'online') {
      if (e.key === 'r' || e.key === 'R') {
        if (netClient?.gameState === 'lobby' || netClient?.gameState === 'countdown') {
          audio.playClick();
          isReady = !isReady;
          netClient?.setReady(isReady);
        }
      }
      // Handle stake key during countdown
      if ((e.key === 's' || e.key === 'S') && netClient?.gameState === 'countdown') {
        if (currentEscrowAddress && isWalletConnected()) {
          if (stakeStatus === 'pending' || stakeStatus === 'error') {
            handleStakeToEscrow();
          }
        }
      }
    }

    if (e.key === 'Escape') {
      if (gameMode !== 'menu') {
        // Return to landing page
        if (netClient) {
          netClient.disconnect();
          netClient = null;
        }
        offlineWorld = null;
        onlineWorld = null;
        practiceLobbyCountdown = null;
        practiceIsReady = false;
        gameMode = 'menu';
        showLandingPage();
      }
    }
  });
}

/**
 * Hide landing page and show game
 */
function hideLandingPage(): void {
  showingLanding = false;
  landingPage.classList.add('hidden');
  gameContainer.classList.add('active');
  gameWindow.resize();

  // Hide stats panels during game
  statsPanel.classList.add('hidden');
  practicePanel.classList.add('hidden');

  // Show touch controls on touch devices
  if (touchControls && input.isTouchEnabled()) {
    touchControls.classList.add('active');
  }

  // Update touch buttons for new game state
  updateTouchButtons();
}

/**
 * Show landing page and hide game
 */
function showLandingPage(): void {
  showingLanding = true;
  landingPage.classList.remove('hidden');
  gameContainer.classList.remove('active');
  buttonsEl.style.display = 'flex';
  practiceButton.style.display = 'inline-block';
  loadingEl.classList.remove('active');

  // Hide touch controls
  if (touchControls) {
    touchControls.classList.remove('active');
  }

  // Update touch buttons (will hide them since we're on landing page)
  updateTouchButtons();

  // Refresh wallet UI and stats
  updateWalletUI();
  updateStatsPanel();
  updatePracticePanel();
}

/**
 * Enter the arena (start online mode from landing page)
 * @param payoutMode - 'proportional' (default) or 'winner-takes-all'
 */
async function enterArena(payoutMode: PayoutMode = 'proportional'): Promise<void> {
  // Check if wallet is connected for online play
  if (!isWalletConnected()) {
    // Flash the wallet button to indicate connection is needed
    walletBtn.style.animation = 'none';
    walletBtn.offsetHeight; // Trigger reflow
    walletBtn.style.animation = '';
    walletStatus.textContent = 'Connect wallet to enter arena';
    walletStatus.classList.add('error');

    // Try to auto-connect
    await handleWalletClick();

    // If still not connected, don't proceed
    if (!isWalletConnected()) {
      return;
    }
  }

  buttonsEl.style.display = 'none';
  practiceButton.style.display = 'none';
  loadingEl.classList.add('active');

  // Short delay for loading animation
  setTimeout(() => {
    hideLandingPage();
    startOnlineMode(payoutMode);
  }, 1000);
}

/**
 * Enter practice mode (go to practice lobby first)
 */
function enterPractice(): void {
  buttonsEl.style.display = 'none';
  practiceButton.style.display = 'none';
  loadingEl.classList.add('active');

  // Short delay for loading animation
  setTimeout(() => {
    hideLandingPage();
    startPracticeLobby();
  }, 500);
}

/**
 * Truncate wallet address for display
 */
function truncateAddress(address: string): string {
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

/**
 * Update wallet UI based on connection state
 */
async function updateWalletUI(): Promise<void> {
  console.log('updateWalletUI called, isConnected:', isWalletConnected());
  if (isWalletConnected()) {
    const address = getWalletAddress();
    console.log('Wallet address:', address);
    walletBtn.textContent = truncateAddress(address || '');
    walletBtn.classList.add('connected');

    // Fetch and display balance
    try {
      console.log('Fetching USDC balance for:', address);
      const balance = await getUSDCBalance();
      console.log('USDC balance raw:', balance, 'type:', typeof balance);
      walletStatus.textContent = `${formatUSDC(balance)} USDC`;
      walletStatus.classList.remove('error');
    } catch (e) {
      console.error('Failed to fetch USDC balance:', e);
      walletStatus.textContent = getWalletDisplayName();
      walletStatus.classList.remove('error');
    }
  } else {
    walletBtn.textContent = 'CONNECT WALLET';
    walletBtn.classList.remove('connected');
    walletStatus.textContent = '';
  }
}

/**
 * Update the practice stats panel
 */
function updatePracticePanel(): void {
  // Always show practice panel on landing page
  practicePanel.classList.remove('hidden');

  const stats = loadPracticeStats();

  practiceGamesPlayed.textContent = stats.gamesPlayed.toString();
  practiceWins.textContent = stats.wins.toString();
  practiceWinRate.textContent = `${getWinRate(stats)}%`;
  practiceHighScore.textContent = stats.highScore.toString();
  practiceAvgScore.textContent = getAverageScore(stats).toString();
  practiceBestRank.textContent = stats.bestRank > 0 ? `#${stats.bestRank}` : '-';
}

/**
 * Update the stats panel with player data
 */
async function updateStatsPanel(): Promise<void> {
  if (!isWalletConnected()) {
    statsPanel.classList.add('hidden');
    return;
  }

  const address = getWalletAddress();
  if (!address) {
    statsPanel.classList.add('hidden');
    return;
  }

  // Show panel
  statsPanel.classList.remove('hidden');

  // Load stats
  const stats = loadPlayerStats(address);

  // Update balance
  try {
    const balance = await getUSDCBalance();
    statBalance.textContent = formatUSDC(balance);
  } catch (e) {
    statBalance.textContent = '$-.--';
  }

  // Update total payouts
  statTotalPayouts.textContent = formatCentsAsDollars(stats.totalPayouts);

  // Update unclaimed payouts
  const unclaimed = stats.unclaimedPayouts || 0;
  statUnclaimed.textContent = formatCentsAsDollars(unclaimed);

  // Update claim button state
  if (unclaimed > 0) {
    claimBtn.disabled = false;
    claimBtn.textContent = `CLAIM ${formatCentsAsDollars(unclaimed)}`;
  } else {
    claimBtn.disabled = true;
    claimBtn.textContent = 'NO PAYOUTS TO CLAIM';
  }

  // Update games played
  statGamesPlayed.textContent = stats.gamesPlayed.toString();

  // Update recent matches
  const recentMatches = getRecentMatches(address, 10);
  renderRecentMatches(recentMatches);
}

/**
 * Render recent matches list
 */
function renderRecentMatches(matches: MatchHistoryEntry[]): void {
  if (matches.length === 0) {
    recentMatchesEl.innerHTML = '<div class="no-matches">No matches yet</div>';
    return;
  }

  const html = matches.map((match) => {
    const isWin = match.rank === 1;
    const netResult = match.payout - match.entryFee;
    const resultClass = netResult > 0 ? 'positive' : netResult < 0 ? 'negative' : 'zero';
    const entryClass = isWin ? 'win' : netResult < 0 ? 'loss' : '';

    return `
      <div class="match-entry ${entryClass}">
        <div class="match-info">
          <span class="match-rank">#${match.rank} of ${match.totalPlayers}</span>
          <span class="match-date">${formatRelativeTime(match.timestamp)}</span>
        </div>
        <span class="match-payout ${resultClass}">${formatCentsAsDollars(match.payout)}</span>
      </div>
    `;
  }).join('');

  recentMatchesEl.innerHTML = html;
}

/**
 * Handle wallet button click
 */
async function handleWalletClick(): Promise<void> {
  if (isWalletConnected()) {
    // Disconnect
    disconnectWallet();
    await updateWalletUI();
    await updateStatsPanel();
    adminPanel.classList.remove('visible'); // Hide admin panel on disconnect
    return;
  }

  // Show available wallet options
  const wallets = getAvailableWallets();

  // For now, try auto-connect (injected wallet first since that's what most users have)
  walletBtn.textContent = 'CONNECTING...';
  walletStatus.textContent = '';
  walletStatus.classList.remove('error');

  try {
    // Try to connect in order of preference
    let connected = false;
    let lastError: Error | null = null;

    // First try injected wallet (MetaMask) if available - most users have this
    const injected = wallets.find((w) => w.type === 'injected');
    if (injected && !connected) {
      try {
        console.log('Trying injected wallet (MetaMask)...');
        await connectWallet('injected');
        connected = true;
        console.log('Injected wallet connected successfully');
      } catch (e: any) {
        console.error('Injected wallet failed:', e);
        lastError = e;
        // User rejected or other error - show it
        if (e.code === 4001 || e.message?.includes('rejected')) {
          walletBtn.textContent = 'CONNECT WALLET';
          walletStatus.textContent = 'Connection rejected';
          walletStatus.classList.add('error');
          return;
        }
      }
    }

    // Then try Smart Wallet (Coinbase creates embedded wallet)
    if (!connected) {
      try {
        console.log('Trying Smart Wallet...');
        await connectWallet('smart');
        connected = true;
        console.log('Smart wallet connected successfully');
      } catch (e: any) {
        console.error('Smart wallet failed:', e);
        lastError = e;
      }
    }

    // Finally try Coinbase Wallet
    if (!connected) {
      try {
        console.log('Trying Coinbase Wallet...');
        await connectWallet('coinbase');
        connected = true;
        console.log('Coinbase wallet connected successfully');
      } catch (e: any) {
        console.error('Coinbase wallet failed:', e);
        lastError = e;
      }
    }

    if (connected) {
      await updateWalletUI();
      await updateStatsPanel();
      await updateAdminPanel(); // Show admin panel if admin wallet
    } else {
      throw lastError || new Error('No wallet available');
    }
  } catch (error: any) {
    console.error('Wallet connection failed:', error);
    walletBtn.textContent = 'CONNECT WALLET';
    const errorMsg = error?.message || 'Connection failed';
    walletStatus.textContent = errorMsg.length > 50 ? errorMsg.slice(0, 47) + '...' : errorMsg;
    walletStatus.classList.add('error');
  }
}

// Set up wallet button
walletBtn.addEventListener('click', handleWalletClick);

// Initialize wallet UI and stats panels on load
updateWalletUI();
updateStatsPanel();
updatePracticePanel();

// Initialize touch buttons (hidden on landing page)
// Note: updateTouchButtons is defined later but hoisted as a function declaration

// Set up landing page buttons
playButton.addEventListener('click', () => enterArena('proportional'));
wtaButton.addEventListener('click', () => enterArena('winner-takes-all'));
practiceButton.addEventListener('click', enterPractice);

// Claim button handler
claimBtn.addEventListener('click', async () => {
  if (claimBtn.disabled) return;

  const address = getWalletAddress();
  if (!address) return;

  // TODO: Implement actual blockchain claim transaction
  // For now, just show a message that claiming will be implemented
  claimBtn.textContent = 'CLAIMING...';
  claimBtn.disabled = true;

  // Placeholder - in production this would call the contract
  console.log('Claim payouts clicked - implement blockchain transaction');

  // Refresh stats after a short delay
  setTimeout(() => {
    updateStatsPanel();
  }, 1000);
});

// Admin panel handlers
adminCloseBtn.addEventListener('click', () => {
  adminPanel.classList.remove('visible');
});

adminRefreshBtn.addEventListener('click', () => {
  updateAdminPanel();
});

/**
 * Update the admin panel with contract and aggregate stats
 */
async function updateAdminPanel(): Promise<void> {
  // Only show for admin wallet
  if (!isWalletConnected() || !isAdmin()) {
    adminPanel.classList.remove('visible');
    return;
  }

  // Show admin panel
  adminPanel.classList.add('visible');

  try {
    // Get contract data
    const [devFees, contractBalance] = await Promise.all([
      getAccumulatedDevFees(),
      getContractUSDCBalance(),
    ]);

    // Update treasury section
    adminDevFees.textContent = formatUSDC(devFees);
    adminContractBalance.textContent = formatUSDC(contractBalance);

    // Calculate total wagered (contract balance - dev fees is roughly player funds)
    const playerFunds = contractBalance > devFees ? contractBalance - devFees : 0n;
    adminTotalWagered.textContent = formatUSDC(playerFunds);

    // Aggregate stats from all localStorage entries
    const aggregateStats = getAggregatePlayerStats();

    adminTotalGames.textContent = aggregateStats.totalGames.toString();
    adminTotalPlayers.textContent = aggregateStats.uniquePlayers.toString();
    adminTotalPayouts.textContent = formatCentsAsDollars(aggregateStats.totalPayouts);
    adminTotalOwed.textContent = formatCentsAsDollars(aggregateStats.totalUnclaimed);
    adminUniqueWinners.textContent = aggregateStats.uniqueWinners.toString();
    adminPendingClaims.textContent = aggregateStats.pendingClaims.toString();

    // Render top earners leaderboard
    renderAdminLeaderboard(aggregateStats.topEarners);
  } catch (e) {
    console.error('Error updating admin panel:', e);
  }
}

/**
 * Get aggregate stats from all player localStorage entries
 */
function getAggregatePlayerStats(): {
  totalGames: number;
  uniquePlayers: number;
  totalPayouts: number;
  totalUnclaimed: number;
  uniqueWinners: number;
  pendingClaims: number;
  topEarners: { address: string; earnings: number }[];
} {
  const stats = {
    totalGames: 0,
    uniquePlayers: 0,
    totalPayouts: 0,
    totalUnclaimed: 0,
    uniqueWinners: 0,
    pendingClaims: 0,
    topEarners: [] as { address: string; earnings: number }[],
  };

  const playerEarnings: Map<string, number> = new Map();
  const winners: Set<string> = new Set();

  // Scan localStorage for player stats
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key || !key.startsWith('mine-or-die-stats-')) continue;

    try {
      const data = JSON.parse(localStorage.getItem(key) || '{}');
      const address = key.replace('mine-or-die-stats-', '');

      stats.uniquePlayers++;
      stats.totalGames += data.gamesPlayed || 0;
      stats.totalPayouts += data.totalPayouts || 0;

      const unclaimed = data.unclaimedPayouts || 0;
      stats.totalUnclaimed += unclaimed;
      if (unclaimed > 0) {
        stats.pendingClaims++;
      }

      if (data.wins > 0) {
        winners.add(address);
      }

      const earnings = data.totalPayouts || 0;
      if (earnings > 0) {
        playerEarnings.set(address, earnings);
      }
    } catch (e) {
      // Skip invalid entries
    }
  }

  stats.uniqueWinners = winners.size;

  // Sort and get top 10 earners
  stats.topEarners = Array.from(playerEarnings.entries())
    .map(([address, earnings]) => ({ address, earnings }))
    .sort((a, b) => b.earnings - a.earnings)
    .slice(0, 10);

  return stats;
}

/**
 * Render admin leaderboard
 */
function renderAdminLeaderboard(topEarners: { address: string; earnings: number }[]): void {
  if (topEarners.length === 0) {
    adminLeaderboard.innerHTML = `
      <div class="leaderboard-entry">
        <span class="rank">-</span>
        <span class="address">No data yet</span>
        <span class="earnings">$0.00</span>
      </div>
    `;
    return;
  }

  const html = topEarners.map((player, index) => {
    const shortAddr = `${player.address.slice(0, 6)}...${player.address.slice(-4)}`;
    return `
      <div class="leaderboard-entry">
        <span class="rank">#${index + 1}</span>
        <span class="address">${shortAddr}</span>
        <span class="earnings">${formatCentsAsDollars(player.earnings)}</span>
      </div>
    `;
  }).join('');

  adminLeaderboard.innerHTML = html;
}

/**
 * Update touch action buttons visibility and state based on game mode
 */
function updateTouchButtons(): void {
  if (!touchReadyBtn || !touchFundBtn || !touchBackBtn) return;

  // Only show touch buttons on touch-enabled devices
  if (!input.isTouchEnabled()) {
    touchReadyBtn.classList.add('hidden');
    touchFundBtn.classList.add('hidden');
    touchBackBtn.classList.add('hidden');
    return;
  }

  // Always show back button when in game (not on landing page)
  if (!showingLanding) {
    touchBackBtn.classList.remove('hidden');
  } else {
    touchBackBtn.classList.add('hidden');
  }

  // Ready button visibility
  const showReady =
    (gameMode === 'practice-lobby') ||
    (gameMode === 'online' && (netClient?.gameState === 'lobby' || netClient?.gameState === 'countdown'));

  if (showReady) {
    touchReadyBtn.classList.remove('hidden');
    // Update ready button state
    const isCurrentlyReady = gameMode === 'practice-lobby' ? practiceIsReady : isReady;
    if (isCurrentlyReady) {
      touchReadyBtn.textContent = 'UNREADY';
      touchReadyBtn.classList.add('active');
    } else {
      touchReadyBtn.textContent = 'READY';
      touchReadyBtn.classList.remove('active');
    }
  } else {
    touchReadyBtn.classList.add('hidden');
  }

  // Fund button visibility (only during online countdown with wallet connected and escrow available)
  const showFund =
    gameMode === 'online' &&
    netClient?.gameState === 'countdown' &&
    currentEscrowAddress &&
    isWalletConnected();

  if (showFund) {
    touchFundBtn.classList.remove('hidden');
    // Update fund button state
    touchFundBtn.classList.remove('staked', 'staking');
    if (stakeStatus === 'staked') {
      touchFundBtn.textContent = 'FUNDED';
      touchFundBtn.classList.add('staked');
    } else if (stakeStatus === 'staking') {
      touchFundBtn.textContent = 'FUNDING...';
      touchFundBtn.classList.add('staking');
    } else if (stakeStatus === 'error') {
      touchFundBtn.textContent = 'RETRY FUND';
    } else {
      touchFundBtn.textContent = 'FUND $1';
    }
  } else {
    touchFundBtn.classList.add('hidden');
  }
}

/**
 * Handle touch ready button click
 */
function handleTouchReadyClick(): void {
  if (gameMode === 'practice-lobby' && netClient) {
    audio.playClick();
    practiceIsReady = !practiceIsReady;
    netClient.setReady(practiceIsReady);
  } else if (gameMode === 'online' && netClient) {
    if (netClient.gameState === 'lobby' || netClient.gameState === 'countdown') {
      audio.playClick();
      isReady = !isReady;
      netClient.setReady(isReady);
    }
  }
  updateTouchButtons();
}

/**
 * Handle touch fund button click
 */
function handleTouchFundClick(): void {
  if (gameMode === 'online' && netClient?.gameState === 'countdown') {
    if (currentEscrowAddress && isWalletConnected()) {
      if (stakeStatus === 'pending' || stakeStatus === 'error') {
        handleStakeToEscrow();
      }
    }
  }
  updateTouchButtons();
}

/**
 * Handle touch back button click (return to menu)
 */
function handleTouchBackClick(): void {
  if (gameMode !== 'menu') {
    audio.playClick();
    if (netClient) {
      netClient.disconnect();
      netClient = null;
    }
    offlineWorld = null;
    onlineWorld = null;
    practiceLobbyCountdown = null;
    practiceIsReady = false;
    gameMode = 'menu';
    showLandingPage();
  }
}

// Set up touch action button handlers
if (touchReadyBtn) {
  touchReadyBtn.addEventListener('click', handleTouchReadyClick);
  touchReadyBtn.addEventListener('touchend', (e) => {
    e.preventDefault();
    handleTouchReadyClick();
  });
}

if (touchFundBtn) {
  touchFundBtn.addEventListener('click', handleTouchFundClick);
  touchFundBtn.addEventListener('touchend', (e) => {
    e.preventDefault();
    handleTouchFundClick();
  });
}

if (touchBackBtn) {
  touchBackBtn.addEventListener('click', handleTouchBackClick);
  touchBackBtn.addEventListener('touchend', (e) => {
    e.preventDefault();
    handleTouchBackClick();
  });
}

// Set up guide toggle
if (guideToggle && guideContent) {
  guideToggle.addEventListener('click', () => {
    guideToggle.classList.toggle('expanded');
    guideContent.classList.toggle('expanded');
  });
}

// Set up game loop
gameLoop.setFrameStart(frameStart);
gameLoop.setFixedUpdate(fixedUpdate);
gameLoop.setRender(render);

// Set up keyboard shortcuts
setupKeyboardShortcuts();

// Start!
console.log('Mine or Die');
gameLoop.start();
